import { CloudArrowUpIcon, VariableIcon } from '@heroicons/react/20/solid'
import { codeDefault } from '@ui/lib/reactFlow/helpers'
import { IconType } from 'react-icons/lib'
import { SlMagicWand } from 'react-icons/sl'
import { NodeTypes } from 'reactflow'

import { CodeTransformType, NodeTypeEnum } from '@ui/interfaces'

import { CodeNode } from './CodeNode'
// import { DragNode } from './DragNode'
import { ExportNode } from './ExportNode'
import { FirehoseNode } from './FirehoseNode'
import { PlaceholderNode } from './PlaceholderNode'
import { TransformationNode } from './TransformationNode'

type ReactIconType = React.ForwardRefExoticComponent<
  Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
    title?: string | undefined
    titleId?: string | undefined
  } & React.RefAttributes<SVGSVGElement>
>

type defaultNodeStateType = {
  [key: string]: Record<string, boolean | ReactIconType | string | IconType | object>
}

const nodeTypes: NodeTypes = {
  [NodeTypeEnum.FIREHOSE]: FirehoseNode,
  [NodeTypeEnum.PLACEHOLDER]: PlaceholderNode,
  [NodeTypeEnum.CODE]: CodeNode,
  [NodeTypeEnum.EXPORT]: ExportNode,
  [NodeTypeEnum.TRANSFORM]: TransformationNode,
  // [NodeTypeEnum.DRAG]: DragNode,
}

export default nodeTypes

interface GenerateInitialCodeProps {
  type: CodeTransformType
  serviceName?: string
  name?: string
  sampleRate?: number
}
export const generateInitialCode = ({ type, serviceName, name, sampleRate }: GenerateInitialCodeProps) => {
  switch (type) {
    case CodeTransformType.DROP:
      return `
/***
 * You have access to the following inputs:
* - \`metadata\`:  { timestamp, datatype }
* - \`record\`:      { ... }
* 
* Play around with the below snippet, or try to
* console.log('metadata', JSON.stringify(record, undefined, 4))
*/

// These are the key attributes of an opentelemetry formatted record
const { attributes, resource, body } = record

// Drop the trace based on some criteria
if (
  Array.isArray(trace.spans) && 
  trace.spans.find(
    (span) => 
      span.resource["service.name"] === '${serviceName}' &&
      span.name === '${name}'
  )
) {
  return null
}

return record
`
    case CodeTransformType.SAMPLE:
      return `
/***
 * You have access to the following inputs:
 * - \`metadata\`:  { timestamp, datatype }
 * - \`record\`:      { ... }
 * 
 * Play around with the below snippet, or try to
 * console.log('metadata', JSON.stringify(record, undefined, 4))
 * 
 * This is a code snippet that will sample the trace
 */

// These are the key attributes of an opentelemetry formatted record
const { attributes, resource, body } = record

if (
  Array.isArray(trace.spans) && 
  trace.spans.find(
    (span) => 
      span.resource["service.name"] === '${serviceName}' &&
      span.name === '${name}'
  ) && 
  Math.random() < ${sampleRate ?? 0.5}
) {
  // Sample the trace based on some criteria
  return record
}

return record
`
    case CodeTransformType.CODE:
      return `
/***
 * You have access to the following inputs:
 * - \`metadata\`:  { timestamp, datatype }
 * - \`record\`:      { ... }
 * 
 * Play around with the below snippet, or try to
 * console.log('metadata', JSON.stringify(record, undefined, 4))
 * 
 * This is a code snippet that will add code to the trace
 */

// These are the key attributes of an opentelemetry formatted record
const { attributes, resource, body } = record

attributes.datable = 'can manipulate your data!'

return {
  ...record,
  attributes
}
`

    default:
      return codeDefault
  }
}

export const nodeDefaultStates: defaultNodeStateType = {
  [NodeTypeEnum.CODE]: { code: codeDefault, description: '', icon: VariableIcon, name: 'Code', undeletable: false },
  [NodeTypeEnum.EXPORT]: { config: {}, data: {}, icon: CloudArrowUpIcon, name: 'Export', undeletable: false },
  template: { icon: SlMagicWand, name: 'Template', undeletable: false },
}
